import type { ApiAddress } from '#/api/types/ApiAddress';
import type { ApiRemoteOptionsWithFormData } from '#/api/types/ApiRemoteOptions';
import type { I18nFn } from '#/i18n/types/I18nFn';
import type { I18nLocale } from '#/i18n/types/I18nLocale';
import type { OrgDashboardApiOrg } from '#/modules/orgDashboard/api/types/OrgDashboardApiOrg';
import { getSalaryPeriodHumanName } from '#/utils/constants/general/salaryPeriods';

type PaidListingType = 'JOB' | 'INTERNSHIP';

function listingHasUSPrice(
  org: OrgDashboardApiOrg,
  address: ApiAddress,
  remoteOptions: ApiRemoteOptionsWithFormData,
): boolean {
  if (org.address.country === 'US') {
    return true;
  }
  if (remoteOptions.locationType !== null) {
    // is null when address field untouched
    if (remoteOptions.locationType === 'ONSITE') {
      if (address.country === 'US') {
        return true;
      }
    } else if (remoteOptions.remoteZone !== undefined) {
      // REMOTE, HYBRID, with remoteZone defined
      // remoteOptions.useOrgAddress is only used for WORLD, COUNTRY, STATE and is stale otherwise
      const useOrgAddress =
        remoteOptions.remoteZone &&
        ['WORLD', 'COUNTRY', 'STATE'].includes(remoteOptions.remoteZone) &&
        remoteOptions.useOrgAddress;
      // address is stale when useOrgAddress is true
      if (!useOrgAddress && address.country === 'US') {
        return true;
      }
      if (remoteOptions.remoteCountry === 'US') {
        return true;
      }
    }
  }
  return false;
}

function singleListingPrice(
  org: OrgDashboardApiOrg,
  listingType: PaidListingType,
  address: ApiAddress,
  remoteOptions: ApiRemoteOptionsWithFormData,
): { dollarPrice: number; creditPrice: number } {
  const usPrice = listingHasUSPrice(org, address, remoteOptions);
  if (usPrice) {
    return {
      dollarPrice: org.prices.usListings[listingType],
      creditPrice:
        org.prices.usListings[listingType] > 0 &&
        (org.promoCredits[listingType] > 0 || org.credits[listingType] > 0)
          ? 1
          : 0,
    };
  }
  return {
    dollarPrice: org.prices.listings[listingType],
    creditPrice:
      org.prices.listings[listingType] > 0 &&
      (org.promoCredits[listingType] > 0 || org.credits[listingType] > 0)
        ? 1
        : 0,
  };
}

export function listingPriceInfo(
  formValues: {
    addresses: ApiAddress[];
    remoteOptions: ApiRemoteOptionsWithFormData[];
  },
  listingType: PaidListingType,
  org: OrgDashboardApiOrg,
): { dollarPrice: number; creditPrice: number }[] {
  return formValues.remoteOptions.map((remoteOptions, index) =>
    singleListingPrice(
      org,
      listingType,
      formValues.addresses[index],
      remoteOptions,
    ),
  );
}

export function listingTotalPriceInfo(
  formValues: {
    addresses: ApiAddress[];
    remoteOptions: ApiRemoteOptionsWithFormData[];
  },
  listingType: PaidListingType,
  org: OrgDashboardApiOrg,
): { dollarPrice: number; creditPrice: number } {
  return listingPriceInfo(formValues, listingType, org).reduce(
    (acc, val) => ({
      dollarPrice: val.dollarPrice + acc.dollarPrice,
      creditPrice: val.creditPrice + acc.creditPrice,
    }),
    { dollarPrice: 0, creditPrice: 0 },
  );
}

// Form helper
export function pricingMessage({
  i18n,
  dollarPrice,
  creditPrice,
}: {
  i18n: I18nFn;
  dollarPrice: number;
  creditPrice: number;
}) {
  return (
    /* TRANSLATORS : in "add to cart" button when org admin creates new listing */
    creditPrice
      ? i18n('$%(dollarPrice)s USD or %(creditPrice)s %(creditLabel)s', {
          dollarPrice,
          creditPrice,
          creditLabel: creditPrice > 1 ? i18n('credits') : i18n('credit'),
        })
      : `$${dollarPrice} USD`
  );
}

// TODO: refactor into generic helper
export function formatSalary(
  locale: I18nLocale,
  salary: number,
  _country: string,
  currency?: string | null,
) {
  const hasDecimal = salary % 1 > 0;

  // round salary unless it's hourly, in which case allow decimals
  const intSalary = hasDecimal ? Number(salary) : Math.round(salary);

  if (currency) {
    if (currency === 'USD') {
      const dollarString = intSalary.toLocaleString(locale, {
        minimumFractionDigits: hasDecimal ? 2 : 0,
      });

      // String.prototype.toLocaleString() uses non-breaking spaces
      // so we're setting it manually here for the sake of consistency
      return `USD\u00A0$${dollarString}`;
    }

    return intSalary.toLocaleString(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'code',
      minimumFractionDigits: hasDecimal ? 2 : 0,
    });
  }

  return intSalary.toLocaleString(locale, {
    minimumFractionDigits: hasDecimal ? 2 : 0,
  });
}

export function formatSalaryRange(
  i18n: I18nFn,
  locale: I18nLocale,
  salaryMinimum: number | string | null | undefined,
  salaryMaximum: number | string | null | undefined,
  country: string,
  currency: string | null | undefined,
) {
  const salaryMinimumAsNumber = Number(salaryMinimum);
  const salaryMaximumAsNumber = Number(salaryMaximum);

  if (
    salaryMinimumAsNumber &&
    salaryMaximumAsNumber &&
    salaryMinimumAsNumber !== salaryMaximumAsNumber
  ) {
    return `${formatSalary(locale, salaryMinimumAsNumber, country, currency)} - ${
      currency === 'USD' ? '$' : ''
    }${formatSalary(locale, salaryMaximumAsNumber, country)}`;
  }

  if (
    salaryMinimumAsNumber &&
    salaryMaximumAsNumber &&
    salaryMinimumAsNumber === salaryMaximumAsNumber
  ) {
    return formatSalary(locale, salaryMinimumAsNumber, country, currency);
  }

  if (salaryMinimumAsNumber) {
    return i18n('At least %(salaryMinimum)s', {
      salaryMinimum: formatSalary(
        locale,
        salaryMinimumAsNumber,
        country,
        currency,
      ),
    });
  }

  if (salaryMaximumAsNumber) {
    return i18n('Up to %(salaryMaximum)s', {
      salaryMaximum: formatSalary(
        locale,
        salaryMaximumAsNumber,
        country,
        currency,
      ),
    });
  }

  return '';
}

export function formatSalaryRangeWithPeriodHumanName(
  i18n: I18nFn,
  locale: I18nLocale,
  salaryMinimum: number | string | null | undefined,
  salaryMaximum: number | string | null | undefined,
  country: string,
  currency: string | null | undefined,
  salaryPeriod: string,
) {
  const salaryText = formatSalaryRange(
    i18n,
    locale,
    salaryMinimum,
    salaryMaximum,
    country,
    currency,
  );

  const periodText = salaryPeriod
    ? ` / ${getSalaryPeriodHumanName(i18n, salaryPeriod)}`
    : '';

  return salaryText + periodText;
}
